import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
    Box,
    Button,
    Link,
    Radio,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import Alert from '/@/components/bayer/alert';
import ChipLabel, { CHIP_COLORS } from '/@/components/chip-label';
import Icon from '/@/components/icon';
import RecommendationCommentsButton from '/@/components/recommendation-comments-button';
import SharePopup from '/@/components/share-popup';
import settings from '/@/core/settings';
import { useProcessRecommendation } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useRecommendationCommentsPermissions } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import type { ApplicationRegister } from '/@/types/application-register';
import type { ApplicationReport } from '/@/types/application-report';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Recommendation } from '/@/types/recommendation';
import { formatDate } from '/@/utils';
import { useMyRecommendationPDF, useWhatIsRecommedation } from '/@/views/dashboard/components/recommendations-list';
import RecommendationMoreOptions from '/@/views/dashboard/components/recommendations-list/components/more-options';
import { seedPdfVersion } from '/@/views/recommendations/seed-pdf-version';
import type { CropMonitoringFormType } from '../../../creation-dialog/types';
import CreateRegistryApplication from './add/application';
import {
    type FieldRegistryFlow,
    FieldRegistryFlowContext,
    startFieldRegistryFlow,
    trackFormOpened,
} from './add/flow-tracking';
import CreateRegistryPlanting from './add/planting';
import {
    canRegisterRecommendation,
    getCropProtectionProductNames,
    getRegisteredProductNames,
    getRegistrationKind,
} from './recommendation-helpers';

/** Map the creator's user-node type to the matching "Creada por" chip color. */
const creatorChipType = (type?: string): keyof typeof CHIP_COLORS => {
    switch (type) {
        case 'ADVISOR':
            return 'advisor';
        case 'RV':
            return 'rv';
        case 'CONSULTANT':
            return 'consultant';
        default:
            return 'gray';
    }
};

const RecommendationRow = ({
    recommendation,
    onSelectForShare,
    onSelectForDownload,
    showRadio,
    radioSelected,
    radioDisabled,
    onRadioSelect,
}: {
    recommendation: Recommendation;
    onSelectForShare: (recommendation: Recommendation) => void;
    onSelectForDownload: (recommendation: Recommendation) => void;
    /** When multiple recos share a card, a radio picks which one to "Registrar lo aplicado". */
    showRadio: boolean;
    radioSelected: boolean;
    radioDisabled: boolean;
    onRadioSelect: () => void;
}) => {
    const { data: ownNode } = useOwnUserNode();
    const processRecommendation = useProcessRecommendation();
    const processedRecommendation = processRecommendation(recommendation);
    const whatIsRecommedation = useWhatIsRecommedation(recommendation);
    const { shouldShowCommentsButton } = useRecommendationCommentsPermissions(
        recommendation.commentsStatus,
        recommendation.userNode
    );

    const isShared = Boolean(recommendation.sharedStatus);
    const createdBy = recommendation.createdBy;
    const isCreatedByCurrentUser = ownNode?.id === createdBy?.id;
    const creatorName = isCreatedByCurrentUser
        ? _('Vos')
        : createdBy
          ? [createdBy.user?.firstName, createdBy.user?.lastName].filter(Boolean).join(' ') || _('sin datos')
          : _('sin datos');

    return (
        <TableRow
            hover
            sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
            onClick={() => processedRecommendation.onClick(processedRecommendation)}
        >
            {showRadio && (
                <TableCell padding='checkbox' onClick={(event) => event.stopPropagation()}>
                    <Radio
                        size='small'
                        color='secondary'
                        checked={radioSelected}
                        disabled={radioDisabled}
                        onChange={onRadioSelect}
                    />
                </TableCell>
            )}
            <TableCell>
                <ChipLabel
                    label={processedRecommendation.iconLabel}
                    type={processedRecommendation.type}
                    icon={<Icon type={processedRecommendation.icon} size='xs' theme={settings.app.theme} />}
                />
            </TableCell>
            <TableCell>
                <Typography variant='body2' color='primary'>
                    {formatDate(new Date(recommendation.created))}
                </Typography>
            </TableCell>
            <TableCell>
                <Typography variant='body2' color='primary'>
                    {whatIsRecommedation.title}
                </Typography>
                {whatIsRecommedation.subtitle && (
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {whatIsRecommedation.subtitle}
                    </Typography>
                )}
            </TableCell>
            <TableCell>
                <ChipLabel label={creatorName} type={creatorChipType(createdBy?.type)} />
            </TableCell>
            <TableCell>
                <Tooltip
                    arrow
                    title={
                        isShared
                            ? _('Ya compartiste esta recomendación.')
                            : _('Todavía no compartiste esta recomendación.')
                    }
                >
                    <Box component='span' display='inline-block'>
                        <ChipLabel
                            label={isShared ? _('Compartida') : _('No compartida')}
                            type={isShared ? 'success' : 'danger'}
                        />
                    </Box>
                </Tooltip>
            </TableCell>
            <TableCell align='right'>
                <Stack direction='row' spacing={0.5} alignItems='center' justifyContent='flex-end'>
                    {shouldShowCommentsButton && (
                        <RecommendationCommentsButton
                            commentsStatus={recommendation.commentsStatus}
                            onOpenDrawer={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                processedRecommendation.onClick(processedRecommendation, { openComments: true });
                            }}
                            variant='list'
                        />
                    )}
                    <RecommendationMoreOptions
                        recommendation={recommendation}
                        onClickRedirect={() => processedRecommendation.onClick(processedRecommendation)}
                        downloadRecommendation={() => onSelectForDownload(recommendation)}
                        shareRecommendation={() => onSelectForShare(recommendation)}
                    />
                </Stack>
            </TableCell>
        </TableRow>
    );
};

/**
 * Mobile-only per-recommendation card: same data every desktop `RecommendationRow` already
 * computes (category chip, creator chip, shared-status chip), laid out as label/value rows
 * with "Abrir recomendación" / "Registrar lo aplicado" links instead of table cells + a
 * kebab menu. Share/download/comments actions are dropped here — no mock was given for them
 * on mobile.
 */
const MobileRecommendationCard = ({
    recommendation,
    canRegister,
    onRegister,
}: {
    recommendation: Recommendation;
    canRegister: boolean;
    onRegister: () => void;
}) => {
    const { data: ownNode } = useOwnUserNode();
    const processRecommendation = useProcessRecommendation();
    const processedRecommendation = processRecommendation(recommendation);
    const whatIsRecommedation = useWhatIsRecommedation(recommendation);

    const isShared = Boolean(recommendation.sharedStatus);
    const createdBy = recommendation.createdBy;
    const isCreatedByCurrentUser = ownNode?.id === createdBy?.id;
    const creatorName = isCreatedByCurrentUser
        ? _('Vos')
        : createdBy
          ? [createdBy.user?.firstName, createdBy.user?.lastName].filter(Boolean).join(' ') || _('sin datos')
          : _('sin datos');

    return (
        <Stack spacing={1} sx={{ border: 1, borderColor: 'primary.mui.stroke', borderRadius: 2, p: 2, width: '100%' }}>
            <ChipLabel
                label={processedRecommendation.iconLabel}
                type={processedRecommendation.type}
                icon={<Icon type={processedRecommendation.icon} size='xs' theme={settings.app.theme} />}
            />
            <Stack direction='row' justifyContent='space-between'>
                <Typography variant='body2' color='primary'>
                    {_('Fecha')}
                </Typography>
                <Typography variant='body2' fontWeight='bold' color='primary'>
                    {formatDate(new Date(recommendation.created))}
                </Typography>
            </Stack>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography variant='body2' color='primary'>
                    {_('Recomendación')}
                </Typography>
                <Typography variant='body2' fontWeight='bold' color='primary' sx={{ textAlign: 'right' }}>
                    {whatIsRecommedation.subtitle ?? whatIsRecommedation.title}
                </Typography>
            </Stack>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography variant='body2' color='primary'>
                    {_('Creada por')}
                </Typography>
                <ChipLabel label={creatorName} type={creatorChipType(createdBy?.type)} />
            </Stack>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography variant='body2' color='primary'>
                    {_('Estado')}
                </Typography>
                <ChipLabel
                    label={isShared ? _('Compartida') : _('No compartida')}
                    type={isShared ? 'success' : 'danger'}
                />
            </Stack>
            <Stack direction='row' spacing={2} alignItems='center' justifyContent='space-between'>
                <Link
                    component='button'
                    underline='always'
                    variant='body2'
                    color='secondary'
                    sx={{ textAlign: 'left' }}
                    onClick={() => processedRecommendation.onClick(processedRecommendation)}
                >
                    {_('Abrir recomendación')}
                </Link>

                <Link
                    component={canRegister ? 'button' : 'span'}
                    underline='always'
                    variant='body2'
                    onClick={canRegister ? onRegister : undefined}
                    sx={{
                        color: canRegister ? 'secondary.main' : 'text.disabled',
                        cursor: canRegister ? 'pointer' : 'default',
                        textAlign: 'right',
                    }}
                >
                    {_('Registrar lo aplicado')}
                </Link>
            </Stack>
        </Stack>
    );
};

const buildPlantingInitialValues = (
    recommendation: Recommendation,
    fieldId?: number
): Partial<CropMonitoringFormType> => {
    if (recommendation.cornDensity) {
        return {
            // Create-vs-update is decided in add/planting from the passed cropMonitoring
            // (harvested → create); id here just keeps the form's own field clean.
            id: undefined,
            crop: 'corn',
            plantingDate: recommendation.cornDensity.plantingDate,
            product: recommendation.cornDensity.productProfile,
            environment: recommendation.cornDensity.environment,
            density: recommendation.cornDensity.userDensity || undefined,
            fieldId,
        };
    }
    const cropReco = recommendation.crops?.[0];
    const crop = cropReco?.crop === 'soy' ? 'soy' : 'corn';
    return {
        id: undefined,
        crop,
        plantingDate: cropReco?.plantingDate,
        product: crop === 'corn' ? cropReco?.cornProducts?.[0] : cropReco?.soyProducts?.[0],
        environment: cropReco?.environment,
        fieldId,
    };
};

const RegistryRecommendation = ({
    recommendations,
    fieldId,
    cropMonitoring,
    applications,
    applicationReports,
    expanded,
    onToggle,
}: {
    recommendations: Recommendation[];
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    applications: ApplicationRegister[];
    applicationReports: ApplicationReport[];
    /** Mobile-only collapse state, injected by `Registries`' accordion. */
    expanded?: boolean;
    onToggle?: () => void;
}) => {
    // Share/download plumbing is per-card: a selected recommendation feeds the PDF popups,
    // mirroring the dashboard recommendations list.
    const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | null>(null);
    const [sharePopupOpen, setSharePopupOpen] = useState(false);
    const [downloadPopupOpen, setDownloadPopupOpen] = useState(false);

    const processRecommendation = useProcessRecommendation();
    const processedSelected = selectedRecommendation ? processRecommendation(selectedRecommendation) : null;
    const { handleEmail, handleWhatsApp, handleDownload } = useMyRecommendationPDF(
        selectedRecommendation || undefined,
        'recommendation-registry'
    );
    const selectedPdfVersion = seedPdfVersion(selectedRecommendation);

    // "Registrar lo aplicado": pick a reco (radio when >1), then open the matching prefilled
    // modal. A reco is eligible unless its products/crop-monitoring are already registered.
    const registeredNames = useMemo(
        () => getRegisteredProductNames(applications, applicationReports),
        [applications, applicationReports]
    );
    const canRegister = (recommendation: Recommendation) =>
        canRegisterRecommendation(recommendation, { cropMonitoring, registeredNames });

    const showRadio = recommendations.length > 1;
    const [selectedRegisterId, setSelectedRegisterId] = useState<number | null>(null);
    const firstEligible = recommendations.find(canRegister);
    const registerTarget = showRadio
        ? (recommendations.find((recommendation) => recommendation.id === selectedRegisterId) ?? firstEligible ?? null)
        : (recommendations[0] ?? null);
    const registerEnabled = Boolean(registerTarget && canRegister(registerTarget) && fieldId);

    const [registerModal, setRegisterModal] = useState<'application' | 'planting' | null>(null);
    // Each "Registrar lo aplicado" is an attempt of its own, straight on the form the reco calls for.
    const [registerFlow, setRegisterFlow] = useState<FieldRegistryFlow>();

    const openRegisterModal = (recommendation: Recommendation) => {
        const kind = getRegistrationKind(recommendation) === 'crop_protection' ? 'application' : 'planting';
        const started = startFieldRegistryFlow(
            { fieldId, cropMonitoring, origin: 'recommendation', entrySource: 'field-monitoring' },
            kind
        );
        trackFormOpened(started, kind);
        setRegisterFlow(started);
        setRegisterModal(kind);
    };

    const handleRegisterClick = () => {
        if (!registerTarget || !registerEnabled) return;
        openRegisterModal(registerTarget);
    };

    // Mobile per-card "Registrar lo aplicado" — each card targets itself directly instead of
    // going through the shared radio selection `registerTarget` derives from. Both state
    // updates below land in the same render, so `registerTarget`/`registerModal` are already
    // in sync by the time the create-registry modal renders.
    const openRegisterModalFor = (recommendation: Recommendation) => {
        if (!canRegister(recommendation) || !fieldId) return;
        setSelectedRegisterId(recommendation.id);
        openRegisterModal(recommendation);
    };

    const handleRegisterSuccess = () => {
        setRegisterModal(null);
        // Clear the radio pick so the (now-registered) reco doesn't keep the button disabled —
        // the default falls back to the next eligible reco.
        setSelectedRegisterId(null);
        enqueueSnackbar(_('¡Listo! El registro se guardó en el seguimiento del lote.'), { variant: 'success' });
    };

    if (recommendations.length === 0) return null;

    return (
        <Stack
            spacing={2}
            sx={{
                position: 'relative',
                bgcolor: 'background.paper',
                border: 2,
                borderColor: CHIP_COLORS.danger.background,
                borderRadius: 2,
                boxShadow: '0px 1px 0.5px rgba(0, 0, 0, 0.14)',
                px: 2,
                pt: 3,
                pb: 2,
                // The "Recomendaciones" badge below straddles the top border (top: -14) — on
                // mobile the timeline's between-entries gap is too tight to clear it without
                // overlapping whatever card sits above, so give this one extra breathing room.
                mt: onToggle ? 2.5 : 0,
            }}
        >
            {/* Category badge straddling the top-left border. */}
            <ChipLabel label={_('Recomendaciones')} type='danger' sx={{ position: 'absolute', top: -14, left: 12 }} />

            <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                onClick={onToggle}
                sx={onToggle ? { cursor: 'pointer' } : undefined}
            >
                <Typography variant='h6' color='primary'>
                    {_('Productos recomendados')}
                </Typography>
                {onToggle && (
                    <Stack
                        onClick={(event) => {
                            event.stopPropagation();
                            onToggle();
                        }}
                        sx={{ cursor: 'pointer', display: 'flex' }}
                    >
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </Stack>
                )}
            </Stack>

            {(!onToggle || expanded) &&
                (onToggle ? (
                    <Stack spacing={2} sx={{ width: '100%' }}>
                        {recommendations.map((recommendation) => (
                            <MobileRecommendationCard
                                key={recommendation.id}
                                recommendation={recommendation}
                                canRegister={canRegister(recommendation) && Boolean(fieldId)}
                                onRegister={() => openRegisterModalFor(recommendation)}
                            />
                        ))}
                        <Typography variant='caption' color='primary.mui.inactive'>
                            {_('Registrá si el productor siguió estas recomendaciones, con o sin cambios.')}
                        </Typography>
                    </Stack>
                ) : (
                    <>
                        <Box sx={{ overflow: 'auto', width: '100%', display: 'block' }}>
                            <Table
                                size='small'
                                sx={{
                                    '& td, & th': { borderColor: 'primary.mui.stroke' },
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        {showRadio && <TableCell padding='checkbox' />}
                                        <TableCell>
                                            <Typography variant='caption' fontWeight='bold' color='primary'>
                                                {_('Tipo')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant='caption' fontWeight='bold' color='primary'>
                                                {_('Fecha de creación')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant='caption' fontWeight='bold' color='primary'>
                                                {_('Recomendación')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant='caption' fontWeight='bold' color='primary'>
                                                {_('Creada por')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant='caption' fontWeight='bold' color='primary'>
                                                {_('Estado')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {recommendations.map((recommendation) => (
                                        <RecommendationRow
                                            key={recommendation.id}
                                            recommendation={recommendation}
                                            onSelectForShare={(row) => {
                                                setSelectedRecommendation(row);
                                                setSharePopupOpen(true);
                                            }}
                                            onSelectForDownload={(row) => {
                                                setSelectedRecommendation(row);
                                                setDownloadPopupOpen(true);
                                            }}
                                            showRadio={showRadio}
                                            radioSelected={registerTarget?.id === recommendation.id}
                                            radioDisabled={!canRegister(recommendation)}
                                            onRadioSelect={() => setSelectedRegisterId(recommendation.id)}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>

                        <Alert severity='surfaceVariant'>
                            {_('Registrá si el productor siguió estas recomendaciones, con o sin cambios.')}
                        </Alert>

                        <Stack direction='row' justifyContent='flex-end'>
                            <Tooltip
                                arrow
                                title={
                                    registerEnabled
                                        ? ''
                                        : _(
                                              'No hay recomendaciones para registrar: ya fueron registradas o no son compatibles.'
                                          )
                                }
                            >
                                <Box component='span' display='inline-block'>
                                    <Button
                                        variant='contained'
                                        color='secondary'
                                        disabled={!registerEnabled}
                                        onClick={handleRegisterClick}
                                    >
                                        {_('Registrar lo aplicado')}
                                    </Button>
                                </Box>
                            </Tooltip>
                        </Stack>
                    </>
                ))}

            {selectedRecommendation && (
                <>
                    <SharePopup
                        type={processedSelected?.type}
                        title={_('Compartir tu recomendación')}
                        whatsAppIntro={_('Te envío una recomendación generada en {{ appName }}', {
                            appName: settings.app.name,
                        })}
                        visible={sharePopupOpen}
                        onClose={() => setSharePopupOpen(false)}
                        onEmail={handleEmail}
                        onWhatsApp={handleWhatsApp}
                        recommendation={selectedRecommendation}
                        entrySource='recommendation-registry'
                        version={2}
                        pdfVersion={selectedPdfVersion}
                        mode='share'
                    />
                    <SharePopup
                        type={processedSelected?.type}
                        title={_('Descargar tu recomendación')}
                        visible={downloadPopupOpen}
                        onClose={() => setDownloadPopupOpen(false)}
                        onDownload={handleDownload}
                        recommendation={selectedRecommendation}
                        entrySource='recommendation-registry'
                        {...(selectedPdfVersion === 2
                            ? { version: 2, pdfVersion: selectedPdfVersion, mode: 'generate' as const }
                            : {})}
                    />
                </>
            )}

            <FieldRegistryFlowContext.Provider value={registerFlow}>
                {registerModal === 'application' && registerTarget && (
                    <CreateRegistryApplication
                        open
                        onClose={() => setRegisterModal(null)}
                        onSuccess={handleRegisterSuccess}
                        fieldId={fieldId}
                        initialProductNames={getCropProtectionProductNames(registerTarget)}
                    />
                )}
                {registerModal === 'planting' && registerTarget && (
                    <CreateRegistryPlanting
                        open
                        onClose={() => setRegisterModal(null)}
                        onSuccess={handleRegisterSuccess}
                        fieldId={fieldId}
                        cropMonitoring={cropMonitoring}
                        initialValues={buildPlantingInitialValues(registerTarget, fieldId)}
                    />
                )}
            </FieldRegistryFlowContext.Provider>
        </Stack>
    );
};

export default RegistryRecommendation;
